import axios from 'axios';

// API 기본 설정
// 환경 변수가 없으면 개발 환경에서만 기본값 사용
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8080/api' : '/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 쿠키 전송을 위해 필요
});

// CSRF 토큰 저장
let csrfToken = null;

// 쿠키에서 CSRF 토큰 가져오기
const getCsrfTokenFromCookie = () => {
  const name = 'XSRF-TOKEN';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const token = parts.pop().split(';').shift();
    console.log('CSRF token from cookie:', token);
    return token;
  }
  return null;
};

// CSRF 토큰 가져오기 (API 호출)
const getCsrfToken = async () => {
  try {
    // 먼저 쿠키에서 확인
    let token = getCsrfTokenFromCookie();
    if (token) {
      csrfToken = token;
      return token;
    }

    // 쿠키에 없으면 API 호출
    const response = await api.get('/auth/csrf-token');
    // Spring Security는 X-XSRF-TOKEN 헤더로 반환
    const newToken = response.headers['x-xsrf-token'] || response.data.token;
    if (newToken) {
      csrfToken = newToken;
      console.log('CSRF token updated from API:', csrfToken);
    }
  } catch (error) {
    console.error('Failed to get CSRF token:', error);
  }
  return csrfToken;
};

// 요청 인터셉터 (CSRF 토큰 추가)
api.interceptors.request.use(
  async (config) => {
    if (config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }

    // CSRF 토큰이 필요한 요청에만 추가 (GET 제외, 인증 API 제외)
    if (config.method !== 'get' && 
        !config.url.includes('/auth/login') && 
        !config.url.includes('/auth/logout') && 
        !config.url.includes('/auth/refresh') &&
        !config.url.includes('/auth/csrf-token')) {
      
      // 쿠키에서 먼저 확인
      let token = getCsrfTokenFromCookie();
      if (!token) {
        // 쿠키에 없으면 API 호출
        token = await getCsrfToken();
      }
      
      if (token) {
        config.headers['X-XSRF-TOKEN'] = token; // 올바른 헤더 이름
        console.log('Request CSRF token:', token);
      }
    }

    // Access Token이 있으면 Authorization 헤더에 추가
    const currentToken = localStorage.getItem('authToken');
    if (currentToken) {
      config.headers['Authorization'] = 'Bearer ' + currentToken;
      console.log('Request Authorization:', 'Bearer ' + currentToken.substring(0, 20) + '...');
    } else {
      console.log('No auth token found for request:', config.url);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 토큰 리프레시 중인지 확인하는 플래그
let isRefreshing = false;
let failedQueue = [];

// 대기 중인 요청들을 처리하는 함수
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// 에러 메시지 추출 함수
const extractErrorMessage = (error) => {
  // 백엔드 ErrorResponse 구조에서 메시지 추출
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // HTTP 상태 코드별 기본 메시지
  const status = error.response?.status;
  switch (status) {
    case 400:
      return '잘못된 요청입니다.';
    case 401:
      return '인증이 필요합니다.';
    case 403:
      return '접근 권한이 없습니다.';
    case 404:
      return '요청한 리소스를 찾을 수 없습니다.';
    case 500:
      return '서버 오류가 발생했습니다.';
    default:
      return '알 수 없는 오류가 발생했습니다.';
  }
};

// 에러 코드 추출 함수
const extractErrorCode = (error) => {
  return error.response?.data?.code || error.code || null;
};

// 에러 상세 정보 추출 함수 (입력 검증 오류 등)
const extractErrorDetails = (error) => {
  return error.response?.data?.details || {};
};

// 응답 인터셉터 (토큰 리프레시 및 에러 처리)
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 401 에러: 토큰 리프레시 시도 (단, 로그인/리프레시 요청은 제외)
    if (error.response?.status === 401 && 
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/login') &&
        !originalRequest.url?.includes('/auth/refresh')) {
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await api.post('/auth/refresh');
        const newToken = response.data;

        localStorage.setItem('authToken', newToken);
        processQueue(null, newToken);

        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 에러 객체에 사용자 친화적인 메시지 추가
    error.userMessage = extractErrorMessage(error);
    error.details = extractErrorDetails(error);
    error.code = extractErrorCode(error);

    return Promise.reject(error);
  }
);

// API 에러 처리 헬퍼 함수 (컴포넌트에서 사용)
export const handleApiError = (error, customMessage = null) => {
  const message = customMessage || error.userMessage || extractErrorMessage(error);
  const details = error.details || extractErrorDetails(error);
  const code = error.code || extractErrorCode(error);
  
  return {
    message,
    details,
    code,
    statusCode: error.response?.status,
    hasDetails: Object.keys(details).length > 0
  };
};

// 공통 에러 표시 함수 (alert 대신 사용)
export const showApiError = (error, customMessage = null) => {
  const errorInfo = handleApiError(error, customMessage);
  
  // details만 표시
  let errorMessage = '';
  
  // code가 있으면 추가
  if (errorInfo.code) {
    errorMessage = `[${errorInfo.code}]`;
  }
  
  // details가 있으면 표시
  if (errorInfo.hasDetails) {
    const detailsList = Object.entries(errorInfo.details)
      .map(([field, message]) => `• ${field}: ${message}`)
      .join('\n');
    
    if (errorMessage) {
      errorMessage = `${errorMessage}\n\n상세 오류:\n${detailsList}`;
    } else {
      errorMessage = `상세 오류:\n${detailsList}`;
    }
  } else {
    // details가 없으면 기본 메시지
    errorMessage = errorMessage || '오류가 발생했습니다.';
  }
  
  alert(errorMessage);
  
  return errorInfo;
};

// JWT 토큰에서 사용자 정보 추출
const parseJWT = (token) => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
};

// 인증 관련 API
export const authAPI = {
  // 로그인
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    const accessToken = response.data;
    
    // JWT 토큰에서 사용자 정보 추출
    const payload = parseJWT(accessToken);
    if (payload && payload.userDto) {
      const userData = {
        id: payload.userDto.id,
        username: payload.userDto.username,
        name: payload.userDto.name,
        role: payload.userDto.role
      };
      
      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return { accessToken, user: userData };
    }
    
    throw new Error('Invalid token format');
  },

  // 로그아웃
  logout: async () => {
    try {
      // 백엔드의 JwtLogoutHandler가 처리하는 POST 요청
      const response = await api.post('/auth/logout');
      console.log('Logout response:', response);
    } catch (error) {
      console.error('Logout API error:', error);
      // API 호출이 실패해도 로컬 정리는 수행
    } finally {
      // 로컬 스토리지 정리
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    }
  },

  // 현재 사용자 정보 조회
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    const accessToken = response.data; // 백엔드에서 String(access token) 반환
    
    // JWT 토큰에서 사용자 정보 추출
    const payload = parseJWT(accessToken);
    if (payload && payload.userDto) {
      const userData = {
        id: payload.userDto.id,
        username: payload.userDto.username,
        name: payload.userDto.name,
        role: payload.userDto.role
      };
      
      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      
      return accessToken; // 토큰 반환 (App.js에서 저장용)
    }
    
    throw new Error('Invalid token format');
  },

  // 토큰 리프레시
  refreshToken: async () => {
    const response = await api.post('/auth/refresh');
    const newAccessToken = response.data; // 백엔드에서 새로운 access token 반환
    localStorage.setItem('authToken', newAccessToken);
    return newAccessToken;
  },
  
  // CSRF 토큰 조회
  getCsrfToken: async () => {
    const response = await api.get('/auth/csrf-token');
    return response.data;
  }
};

// 사용자 관련 API
export const userAPI = {
  // 사용자 생성 (회원가입)
  create: async (userData) => {
    const response = await api.post('/users/signup', userData);
    return response.data;
  },

  // 사용자 목록 조회 (커서 기반 페이지네이션)
  getAll: async (condition = {}) => {
    const params = {
      keyword: condition.keyword || '',
      cursor: condition.cursor || null,
      after: condition.after || null,
      sort: condition.sort || 'name',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit || 20
    };

    const response = await api.get('/users', { params });
    console.log('Users API response:', response.data);

    // 백엔드 응답 구조에 맞게 매핑
    const responseData = response.data;
    return {
      data: responseData.users || [], // 실제 사용자 목록
      nextCursor: responseData.nextCursor,
      hasNext: responseData.hasNext || false,
      size: responseData.size || 0,
      totalCount: responseData.totalCount || 0
    };
  },

  // 특정 사용자 조회
  get: async (id) => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  // 사용자 정보 수정
  update: async (id, userData) => {
    const response = await api.patch(`/users/${id}`, userData);
    return response.data;
  },

  // 사용자 삭제
  delete: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response; // ResponseEntity.noContent().build()이므로 data 없음
  }
};

// 발전소 관련 API
export const powerLocationAPI = {
  // 발전소 생성
  create: async (powerLocationData) => {
    const response = await api.post('/power-locations', powerLocationData);
    return response.data;
  },

  // 발전소 목록 조회 (커서 기반 페이지네이션)
  getAll: async (condition = {}) => {
    const params = {
      keyword: condition.keyword || '',
      cursor: condition.cursor || null,
      after: condition.after || null,
      sortBy: condition.sortBy || 'serialCode',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit || 20
    };

    const response = await api.get('/power-locations', { params });
    console.log('Power locations API response:', response.data);

    // 백엔드 응답 구조에 맞게 매핑
    const responseData = response.data;
    return {
      data: responseData.powerLocations || [], // 실제 발전소 목록
      nextCursor: responseData.nextCursor,
      hasNext: responseData.hasNext || false,
      size: responseData.size || 0,
      totalCount: responseData.totalCount || 0
    };
  },

  // 특정 발전소 조회
  get: async (id) => {
    const response = await api.get(`/power-locations/${id}`);
    return response.data;
  },

  // 발전소 발전 시간 조회
  getGenerationTime: async (id) => {
    const response = await api.get(`/power-locations/${id}/generation-time`);
    return response.data;
  },

  // 발전소 정보 수정
  update: async (id, powerLocationData) => {
    const response = await api.patch(`/power-locations/${id}`, powerLocationData);
    return response.data;
  },

  // 발전소 삭제
  delete: async (id) => {
    const response = await api.delete(`/power-locations/${id}`);
    return response; // ResponseEntity.noContent().build()이므로 data 없음
  }
};

// 사용자-발전소 연결 관련 API
export const userLocationConnectorAPI = {
  // 연결 생성
  create: async (connectionData) => {
    const response = await api.post('/user-location-connectors', connectionData);
    return response.data;
  },

  // 연결 목록 조회 (커서 기반 페이지네이션)
  getAll: async (condition = {}) => {
    const params = {
      keyword: condition.keyword || '',
      cursor: condition.cursor || null,
      after: condition.after || null,
      sortBy: condition.sortBy || 'serialCode',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit || 20
    };

    const response = await api.get('/user-location-connectors', { params });
    console.log('User location connectors API response:', response.data);

    // 백엔드 응답 구조에 맞게 매핑
    const responseData = response.data;
    return {
      data: responseData.content || [], // 실제 연결 목록
      nextCursor: responseData.nextCursor,
      hasNext: responseData.hasNext || false,
      size: responseData.size || 0,
      totalCount: responseData.totalCount || 0
    };
  },

  // 특정 연결 조회
  get: async (id) => {
    const response = await api.get(`/user-location-connectors/${id}`);
    return response.data;
  },

  // 연결 삭제
  delete: async (id) => {
    const response = await api.delete(`/user-location-connectors/${id}`);
    return response; // ResponseEntity.noContent().build()이므로 data 없음
  }
};

// 장비 설정 승인 요청 관련 API
export const approveRequestAPI = {
  // 장비 설정 승인 요청 목록 조회 (커서 기반 페이지네이션)
  getAll: async (condition = {}) => {
    const params = {
      keyword: condition.keyword || '',
      status: condition.status || null,
      cursor: condition.cursor || null,
      after: condition.after || null,
      sort: condition.sort || 'createdAt',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit || 20
    };

    const response = await api.get('/approve-requests', { params });
    console.log('Approve requests API response:', response.data);

    // 백엔드 응답 구조에 맞게 매핑
    const responseData = response.data;
    return {
      data: responseData.approveRequests || [], // 실제 승인 요청 목록
      nextCursor: responseData.nextCursor,
      hasNext: responseData.hasNext || false,
      size: responseData.size || 0,
      totalCount: responseData.totalCount || 0
    };
  },

  // 특정 장비 설정 승인 요청 조회 (상세 정보 포함)
  get: async (id) => {
    const response = await api.get(`/approve-requests/${id}`);
    return response.data;
  },

  // 장비 설정 승인 요청 상태 업데이트 (승인/거부)
  update: async (id, status, latitude = null, longitude = null) => {
    console.log('API update 호출 - ID:', id, 'Status:', status, 'Latitude:', latitude, 'Longitude:', longitude);
    const requestBody = { status };
    
    // 승인 시 좌표 추가
    if (status === 'APPROVED' && latitude !== null && longitude !== null) {
      requestBody.latitude = latitude;
      requestBody.longitude = longitude;
    }
    
    const response = await api.patch(`/approve-requests/${id}`, requestBody);
    console.log('API update 응답:', response);
    console.log('API update 응답 데이터:', response.data);
    return response.data;
  },

  // 장비 설정 승인 요청 삭제
  delete: async (id) => {
    const response = await api.delete(`/approve-requests/${id}`);
    return response; // ResponseEntity.noContent().build()이므로 data 없음
  }
};

// 발전소 상태 조회 관련 API
export const powerLocationStatusAPI = {
  // 발전소 상태 목록 조회 (커서 기반 페이지네이션)
  getStatusList: async (condition = {}) => {
    const params = {
      region: condition.region || null,
      status: condition.status || null,
      keyword: condition.keyword || '',
      cursor: condition.cursor || null,
      after: condition.after || null,
      sortBy: condition.sortBy || 'createdAt',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit ?? null,
    };

    const response = await api.get('/power-locations/status', { params });
    console.log('Power location status API response:', response.data);

    return {
      data: response.data.powerPlants || [],
      nextCursor: response.data.nextCursor,
      hasNext: response.data.hasNext || false,
      size: response.data.size || 0,
      totalCount: response.data.totalCount || 0,
      normalCount: response.data.normalCount || 0,
      errorCount: response.data.errorCount || 0,
      totalPowerGeneration: response.data.totalPowerGeneration || 0,
      averageEfficiencyPercentage: response.data.averageEfficiencyPercentage || 0,
      createdAt: response.data.createdAt
    };
  },

  // 특정 발전소 상태 조회
  getStatus: async (id) => {
    const response = await api.get(`/power-locations/${id}/status`);
    return response.data;
  }
};

// 발전소 상세 정보 조회 관련 API
export const powerPlantDetailAPI = {
  // 발전소 상세 목록 조회 (커서 기반 페이지네이션)
  getDetailsList: async (condition = {}) => {
    const params = {
      region: condition.region || null,
      status: condition.status || null,
      keyword: condition.keyword || '',
      cursor: condition.cursor || null,
      after: condition.after || null,
      sortBy: condition.sortBy || 'createdAt',
      sortDirection: condition.sortDirection || 'DESC',
      limit: condition.limit ?? null,
    };

    const response = await api.get('/power-locations/details', { params });
    console.log('Power plant details API response:', response.data);

    return {
      data: response.data.powerPlants || [],
      nextCursor: response.data.nextCursor,
      hasNext: response.data.hasNext || false,
      size: response.data.size || 0,
      totalCount: response.data.totalCount || 0,
      createdAt: response.data.createdAt
    };
  },

  // 특정 발전소 상세 정보 조회
  getDetail: async (id) => {
    const response = await api.get(`/power-locations/${id}/details`);
    return response.data;
  }
};

// 온도 설정 관련 API
export const temperatureConfigAPI = {
  // 온도 설정 존재 여부 확인
  checkTemperatureConfig: async (id) => {
    const response = await api.get(`/temperature-configs/${id}/check`);
    return response.data;
  },

  // 온도 설정 목록 조회
  getTemperatureList: async (id) => {
    const response = await api.get(`/temperature-configs/${id}`);
    return response.data; // List<TemperatureConfigListDto> 직접 반환
  },

  // 온도 센서 수정
  update: async (id, updateData) => {
    const response = await api.put(`/temperature-configs/${id}`, updateData);
    return response.data; // TemperatureConfigListDto
  },

  // 온도 센서 삭제
  delete: async (id) => {
    const response = await api.delete(`/temperature-configs/${id}`);
    return response;
  }
};

// 패널 설정 관련 API
export const panelConfigAPI = {
  // 패널 설정 목록 조회
  getPanelList: async (id) => {
    const response = await api.get(`/panel-configs/${id}`);
    return response.data; // List<PanelConfigDto> 직접 반환
  },

  // 발전소별 접속반 및 채널 개수 조회
  getCountByPowerLocation: async (powerLocationId) => {
    const response = await api.get(`/panel-configs/count/${powerLocationId}`);
    return response.data;
  },

  // 접속반 수정
  update: async (id, updateData) => {
    const response = await api.put(`/panel-configs/${id}`, updateData);
    return response.data; // PanelConfigDto
  },

  // 접속반 삭제
  delete: async (id) => {
    const response = await api.delete(`/panel-configs/${id}`);
    return response;
  }
};

// 인버터 설정 관련 API
export const inverterConfigAPI = {
  // 인버터 설정 목록 조회
  getInverterList: async (id) => {
    const response = await api.get(`/inverter-configs/${id}`);
    return response.data; // List<InverterConfigDto> 직접 반환
  },

  // 인버터 수정
  update: async (id, updateData) => {
    const response = await api.put(`/inverter-configs/${id}`, updateData);
    return response.data; // InverterConfigDto
  },

  // 인버터 삭제
  delete: async (id) => {
    const response = await api.delete(`/inverter-configs/${id}`);
    return response;
  }
};


// 트렌드 데이터 관련 API
export const trendAPI = {
  // 트렌드 데이터 조회
  getTrendData: async (powerLocationId, params = {}) => {
    const queryParams = {
      type: params.type || null,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      hours: params.hours || 24
    };
    
    const response = await api.get(`/trends/${powerLocationId}`, { params: queryParams });
    return response.data;
  },

  // 트렌드 요약 정보 조회
  getTrendSummary: async (powerLocationId) => {
    const response = await api.get(`/trends/${powerLocationId}/summary`);
    return response.data;
  }
};

// 보고서 관련 API
export const reportAPI = {
  // 인버터 보고서 데이터 조회 (개별 인버터만)
  getInverterReportData: async (inverterId, params = {}) => {
    const queryParams = {
      periodType: params.periodType || 'HOURLY',
      startDate: params.startDate || null,
      endDate: params.endDate || null
    };
    
    const response = await api.get(`/inverter-data/report/${inverterId}`, { params: queryParams });
    return response.data;
  },

  // 인버터 통합 보고서 데이터 조회 (모든 인버터 합산)
  getIntegratedReportData: async (powerLocationId, params = {}) => {
    const queryParams = {
      periodType: params.periodType || 'HOURLY',
      startDate: params.startDate || null,
      endDate: params.endDate || null
    };
    
    const response = await api.get(`/inverter-data/integrated-report/${powerLocationId}`, { params: queryParams });
    return response.data;
  },

  // 환경센서 보고서 데이터 조회
  getEnvironmentalSensorReport: async (powerLocationId, params = {}) => {
    const queryParams = {
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      periodType: params.periodType || 'HOURLY'
    };
    
    const response = await api.get(`/temperature-data/${powerLocationId}/environmental-report`, { params: queryParams });
    return response.data;
  },

  // 기존 보고서 데이터 조회 (호환성 유지)
  getReportData: async (powerLocationId, params = {}) => {
    const queryParams = {
      reportType: params.reportType || null,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      page: params.page || 0,
      size: params.size || 20
    };
    
    const response = await api.get(`/reports/${powerLocationId}`, { params: queryParams });
    return response.data;
  },

  // 보고서 요약 정보 조회
  getReportSummary: async (powerLocationId, params = {}) => {
    const queryParams = {
      startDate: params.startDate || null,
      endDate: params.endDate || null
    };
    
    const response = await api.get(`/reports/${powerLocationId}/summary`, { params: queryParams });
    return response.data;
  },

  // 일일 보고서 조회
  getDailyReport: async (powerLocationId, date) => {
    const response = await api.get(`/reports/${powerLocationId}/daily`, { 
      params: { date: date.toISOString().split('T')[0] }
    });
    return response.data;
  },

  // 월간 보고서 조회
  getMonthlyReport: async (powerLocationId, year, month) => {
    const response = await api.get(`/reports/${powerLocationId}/monthly`, { 
      params: { year, month }
    });
    return response.data;
  }
};

// 에러 관련 API
export const errorAPI = {
  // 에러 데이터 조회
  getErrorData: async (powerLocationId, params = {}) => {
    const queryParams = {
      errorType: params.errorType || null,
      severity: params.severity || null,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      page: params.page || 0,
      size: params.size || 20
    };
    
    const response = await api.get(`/errors/${powerLocationId}`, { params: queryParams });
    return response.data;
  },

  // 에러 요약 정보 조회
  getErrorSummary: async (powerLocationId, params = {}) => {
    const queryParams = {
      startDate: params.startDate || null,
      endDate: params.endDate || null
    };
    
    const response = await api.get(`/errors/${powerLocationId}/summary`, { params: queryParams });
    return response.data;
  },

  // 최근 에러 조회
  getRecentErrors: async (powerLocationId, limit = 10) => {
    const response = await api.get(`/errors/${powerLocationId}/recent`, { 
      params: { limit }
    });
    return response.data;
  }
};

export const inverterDataAPI = {
  getTrendData: async (inverterId) => {
    const response = await api.get(`/inverter-data/trend-data/${inverterId}`);
    return response.data;
  },
  getChartData: async (inverterId, chartType) => {
    const response = await api.get(`/inverter-data/chart-data/${inverterId}`, {
      params: { type: chartType }
    });
    return response.data;
  }
};

// 온도 데이터(환경센서) 관련 API
export const temperatureDataAPI = {
  // 특정 발전소의 온도 데이터 조회
  getTemperatureData: async (powerLocationId) => {
    const response = await api.get(`/temperature-data/${powerLocationId}`);
    return response.data;
  },

  // 시간별 온도 데이터 조회 (온도 타입별)
  getHourlyTemperatureByType: async (powerLocationId, temperatureType) => {
    const response = await api.get(`/temperature-data/${powerLocationId}/hourly/temperature/${temperatureType}`);
    return response.data;
  },

  // 시간별 일사량 데이터 조회 (일사량 타입별)
  getHourlySolarRadiationByType: async (powerLocationId, radiationType) => {
    const response = await api.get(`/temperature-data/${powerLocationId}/hourly/solar-radiation/${radiationType}`);
    return response.data;
  }
};

// 채널 데이터 관련 API
export const channelDataAPI = {
  // PanelConfig ID로 마지막 PanelData와 연결된 ChannelData를 DTO로 조회
  getLatestChannelDataByPanelConfigId: async (panelConfigId) => {
    const response = await api.get(`/channel-data/${panelConfigId}/latest-channel-data`);
    return response.data;
  },

  // 채널 보고서 데이터 조회
  getChannelReport: async (panelConfigId, params = {}) => {
    const queryParams = {
      periodType: params.periodType || 'HOURLY',
      startDate: params.startDate || null,
      endDate: params.endDate || null
    };
    
    const response = await api.get(`/channel-data/report/${panelConfigId}`, { params: queryParams });
    return response.data;
  },

  // 발전소별 정상 접속반 및 채널 개수 조회 (10분 이내 최신 데이터)
  getCountByPowerLocation: async (powerLocationId) => {
    const response = await api.get(`/channel-data/count/${powerLocationId}`);
    return response.data;
  }
};

// 인버터 에러 관련 API
export const inverterErrorAPI = {
  // 인버터 에러 목록 조회
  getInverterErrors: async (inverterConfigId, params = {}) => {
    const queryParams = {
      inverterConfigId: inverterConfigId,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      periodType: params.periodType || null
    };
    
    const response = await api.get('/inverter-errors', { params: queryParams });
    return response.data;
  },

  // 발전소의 전체 인버터 에러 카운트 조회 (홈탭용)
  getInverterErrorCountAll: async (powerLocationId) => {
    const response = await api.get(`/inverter-errors/count/${powerLocationId}/all`);
    return response.data;
  },

  // 개별 인버터 설정의 에러 카운트 조회 (트렌드용)
  getInverterErrorCount: async (inverterConfigId) => {
    const response = await api.get(`/inverter-errors/count/${inverterConfigId}`);
    return response.data;
  }
};

// 환경센서(온도) 에러 관련 API
export const temperatureErrorAPI = {
  // 환경센서 에러 목록 조회
  getTemperatureErrors: async (powerLocationId, params = {}) => {
    const queryParams = {
      powerLocationId: powerLocationId,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      periodType: params.periodType || null
    };
    
    const response = await api.get('/temperature-errors', { params: queryParams });
    return response.data;
  }
};

// 접속반(패널) 에러 관련 API
export const panelErrorAPI = {
  // 접속반 에러 목록 조회
  getPanelErrors: async (panelConfigId, params = {}) => {
    const queryParams = {
      panelConfigId: panelConfigId,
      startDate: params.startDate || null,
      endDate: params.endDate || null,
      periodType: params.periodType || null
    };
    
    const response = await api.get('/panel-errors', { params: queryParams });
    return response.data;
  }
};

// 인버터 실시간 데이터 관련 API
export const inverterRealTimeAPI = {
  // 발전소의 인버터 실시간 데이터 목록 조회
  // { serverTime, items } — serverTime은 PC 시계와 무관한 신선도 판정용
  getInverterRealTimeList: async (powerLocationId) => {
    const response = await api.get(`/inverter-real-time/${powerLocationId}`);
    const data = response.data;
    if (Array.isArray(data)) {
      return { serverTime: null, items: data };
    }
    return {
      serverTime: data?.serverTime ?? null,
      items: Array.isArray(data?.items) ? data.items : [],
    };
  }
};

// 주소 좌표 변환 관련 API (Geocoder)
export const geocoderAPI = {
  // 주소를 좌표로 변환
  getCoordinates: async (address) => {
    const encodedAddress = encodeURIComponent(address);
    const response = await api.get(`/geocoder/${encodedAddress}`);
    return response.data;
  }
};

// 제품(상품) 관련 API
export const productAPI = {
  getList: async () => {
    const response = await api.get('/products');
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },

  create: async (product, thumbnailFile) => {
    const formData = new FormData();
    formData.append(
      'product',
      new Blob([JSON.stringify(product)], { type: 'application/json' })
    );
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }
    const response = await api.post('/products/create', formData);
    return response.data;
  },

  update: async (productId, product, thumbnailFile) => {
    const formData = new FormData();
    formData.append(
      'product',
      new Blob([JSON.stringify(product)], { type: 'application/json' })
    );
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile);
    }
    const response = await api.patch(`/products/${productId}`, formData);
    return response.data;
  },

  delete: async (productId) => {
    const response = await api.patch(`/products/${productId}/delete`, { useYn: false });
    return response.data;
  },
};

// 장바구니 관련 API
export const cartAPI = {
  getMyCart: async () => {
    const response = await api.get('/cart');
    return response.data;
  },

  addItem: async (productId, quantity = 1) => {
    const response = await api.post('/cart/items', { productId, quantity });
    return response.data;
  },

  removeItem: async (cartItemId) => {
    await api.delete(`/cart/items/${cartItemId}`);
  },
};

// 주문/결제 관련 API
export const orderAPI = {
  prepareDirectCheckout: async (productId, quantity = 1, powerLocationId) => {
    const response = await api.post('/orders/checkout/direct/prepare', {
      productId,
      quantity,
      powerLocationId,
    });
    return response.data;
  },

  prepareCartCheckout: async (cartItemIds = null, powerLocationId) => {
    const payload = Array.isArray(cartItemIds) ? { cartItemIds } : {};
    if (powerLocationId != null) {
      payload.powerLocationId = powerLocationId;
    }
    const response = await api.post('/orders/checkout/cart/prepare', payload);
    return response.data;
  },

  confirmPayment: async (paymentId) => {
    const response = await api.post('/orders/checkout/confirm', { paymentId });
    return response.data;
  },

  getOrders: async ({ page = 0, size = 10, userId, buyerName } = {}) => {
    const params = { page, size };
    if (userId != null) {
      params.userId = userId;
    }
    if (buyerName) {
      params.buyerName = buyerName;
    }
    const response = await api.get('/orders', { params });
    return response.data;
  },

  getReceiptUrl: async (orderId) => {
    const response = await api.get(`/orders/${orderId}/receipt-url`);
    return response.data;
  },
};

// 구독 관련 API
export const subscriptionAPI = {
  getMySubscriptions: async () => {
    const response = await api.get('/subscriptions/me');
    return response.data;
  },

  getByPowerLocation: async (powerLocationId) => {
    const response = await api.get(`/subscriptions/power-locations/${powerLocationId}`);
    return response.data;
  },

  updatePowerLocationSubscription: async (powerLocationId, payload) => {
    const response = await api.patch(`/subscriptions/power-locations/${powerLocationId}`, payload);
    return response.data;
  },
};

// 정책 관련 API
export const policyAPI = {
  getActivePolicies: async () => {
    const response = await api.get('/policies');
    return response.data;
  },

  getActivePolicy: async (policyType) => {
    const response = await api.get(`/policies/${policyType}`);
    return response.data;
  },

  listPoliciesByType: async (policyType) => {
    const response = await api.get(`/policies/admin/type/${policyType}`);
    return response.data;
  },

  listAllPolicies: async () => {
    const response = await api.get('/policies/admin');
    return response.data;
  },

  getPolicyById: async (id) => {
    const response = await api.get(`/policies/admin/${id}`);
    return response.data;
  },

  createPolicy: async (policyType, payload) => {
    const response = await api.post(`/policies/${policyType}`, payload);
    return response.data;
  },

  activatePolicy: async (id) => {
    const response = await api.patch(`/policies/admin/${id}/activate`);
    return response.data;
  },

  updatePolicy: async (id, payload) => {
    const response = await api.patch(`/policies/admin/${id}`, payload);
    return response.data;
  },
};

// 공지사항 관련 API
export const notificationAPI = {
  // 공지사항 목록 조회 (페이지네이션)
  getAll: async (page = 0, size = 10) => {
    const response = await api.get('/notifications', {
      params: { page, size }
    });
    return response.data;
  },
  
  // 공지사항 상세 조회
  getById: async (id) => {
    const response = await api.get(`/notifications/${id}`);
    return response.data;
  },
  
  // 공지사항 저장
  save: async (title, content, userId) => {
    const response = await api.post('/notifications/save', {
      title,
      content,
      userId
    });
    return response.data;
  },
  
  // 공지사항 수정
  update: async (id, title, content) => {
    const response = await api.patch(`/notifications/${id}`, {
      title,
      content
    });
    return response.data;
  },
  
  // 공지사항 삭제
  delete: async (id) => {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  }
};

// 알람 관련 API
export const alarmAPI = {
  // 알람 목록 조회 (커서 페이지네이션)
  getAll: async (condition = {}) => {
    const params = {
      userId: condition.userId || null,
      cursor: condition.cursor || null,
      after: condition.after || null,
      limit: condition.limit || 20
    };

    // null 값 제거
    Object.keys(params).forEach(key => params[key] === null && delete params[key]);

    const response = await api.get('/alarms', { params });
    return response.data;
  },

  // 알람 읽음 처리
  markAsRead: async (alarmId) => {
    const response = await api.patch(`/alarms/${alarmId}/read`);
    return response.data;
  }
};

// 날씨 관련 API
export const weatherAPI = {
  // 날씨 정보 조회
  getWeather: async (locationId) => {
    const response = await api.get(`/weather/${locationId}`);
    return response.data;
  }
};

export default api;